import { QuoteResultDto } from 'apiConnector/quote/dto/quoteResultDto';
import { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import { DynamicFormResult } from 'models/forms/DynamicFormResult';
import { EssentialFormResult } from 'models/forms/essential';
import { StaticFormResult } from 'models/forms/StaticFormResult';
import { ReadonlyValueDefinition } from 'models/metadata/readonlyValue.definition';
import { HandledWorkspace } from 'state/forms/models';
import { QuoteDetailsData } from 'state/quotes/details/details.model';
import { QuoteType } from 'state/quotes/infos/infos.model';
import { QuoteResult } from 'state/quotes/results/result.model';

export type CommonQuoteAction = ReturnType<(typeof commonQuoteAction)[keyof typeof commonQuoteAction]>;
export type QuoteAcked = ReturnType<typeof commonQuoteAction.quoteAcked>;
export type QuoteRequested = ReturnType<typeof commonQuoteAction.quoteRequested>;

export type QuoteRequestFailed = ReturnType<typeof commonQuoteAction.quoteRequestFailed>;
export type QuoteResultFailed = ReturnType<typeof commonQuoteAction.quoteResultFailed>;
export type StaticQuoteReceived = ReturnType<typeof commonQuoteAction.quoteReceived<'static'>>;
export type DynamicQuoteReceived = ReturnType<typeof commonQuoteAction.quoteReceived<'dynamic'>>;
export type EssentialQuoteReceived = ReturnType<typeof commonQuoteAction.quoteReceived<'essential'>>;
type FormResultByWorkspace<T extends HandledWorkspace> = T extends 'dynamic'
  ? DynamicFormResult
  : T extends 'static' | 'smartQuote'
    ? StaticFormResult
    : EssentialFormResult;
export const commonQuoteAction = {
  quoteAcked(
    workspace: HandledWorkspace,
    tabId: string,
    quoteId: string,
    quoteType: QuoteType,
    externalQuoteId: string,
    negotiationId: string | undefined,
    estimatedQuotingDurationInMillis: number,
    quoteRequestStartingTimeInMillis: number,
    newVersion: number,
    isExecOnlyAddon: boolean,
    isKidExpected: boolean,
    frontReconciliationId: string,
    quoteDetails: QuoteDetailsData,
    when: Date,
    sidePanelDetails: readonly ReadonlyValueDefinition[] | undefined,
  ) {
    return {
      type: 'QUOTE_ACKED' as const,
      workspace,
      tabId,
      quoteId,
      quoteType,
      externalQuoteId,
      negotiationId,
      estimatedQuotingDurationInMillis,
      quoteRequestStartingTimeInMillis,
      newVersion,
      isExecOnlyAddon,
      isKidExpected,
      frontReconciliationId,
      quoteDetails,
      when,
      sidePanelDetails,
    };
  },
  quoteRequested(
    workspace: HandledWorkspace,
    tabId: string,
    quoteType: QuoteType,
    quoteRequestStartingTimeInMillis: number,
    frontReconciliationId: string,
    isReprice: boolean = false,
  ) {
    return {
      type: 'QUOTE_REQUESTED' as const,
      workspace,
      tabId,
      quoteType,
      quoteRequestStartingTimeInMillis,
      frontReconciliationId,
      isReprice,
    };
  },
  quoteCancelled(
    workspace: HandledWorkspace,
    tabId: string,
    negotiationId?: string,
    quoteId?: string,
    productSubtype?: string,
    productType?: string,
  ) {
    return {
      type: 'QUOTE_CANCELLED' as const,
      tabId,
      workspace,
      negotiationId,
      quoteId,
      productSubtype,
      productType,
    };
  },
  quoteRemoved(workspace: HandledWorkspace, tabId: string, quoteId?: string) {
    return {
      workspace,
      type: 'QUOTE_REMOVED' as const,
      tabId,
      quoteId,
    };
  },
  quoteRequestFailed(
    workspace: HandledWorkspace,
    tabId: string,
    technicalError: ApiErrorMessage,
    frontReconciliationId: string,
  ) {
    return {
      type: 'QUOTE_REQUEST_FAILED' as const,
      workspace,
      tabId,
      technicalError,
      frontReconciliationId,
    };
  },
  quoteResultFailed(
    workspace: HandledWorkspace,
    tabId: string,
    quoteId: string,
    technicalError: ApiErrorMessage,
    when: Date,
  ) {
    return {
      type: 'QUOTE_RESULT_FAILED' as const,
      workspace,
      tabId,
      quoteId,
      technicalError,
      when,
    };
  },
  quoteReceived<T extends HandledWorkspace>(
    workspace: T,
    tabId: string,
    quoteId: string,
    quote: QuoteResultDto<FormResultByWorkspace<T>>,
    when: Date,
  ) {
    return {
      type: 'QUOTE_RECEIVED' as const,
      workspace,
      tabId,
      quoteId,
      quote,
      when,
    };
  },
  previousQuoteLoadRequested(workspace: HandledWorkspace, tabId: string, quoteId: string) {
    return {
      type: 'PREVIOUS_QUOTE_LOAD_REQUESTED' as const,
      workspace,
      tabId,
      quoteId,
    };
  },
  previousQuoteLoaded(workspace: HandledWorkspace, tabId: string, quoteId: string, quote: QuoteResult) {
    return {
      type: 'PREVIOUS_QUOTE_LOADED' as const,
      workspace,
      tabId,
      quoteId,
      quote,
    };
  },
  previousQuoteLoadingFailed(workspace: HandledWorkspace, tabId: string, quoteId: string) {
    return {
      type: 'PREVIOUS_QUOTE_LOADING_FAILED' as const,
      workspace,
      tabId,
      quoteId,
    };
  },
  quoteStale(workspace: HandledWorkspace, tabId: string, quoteId: string, hasFormChanged: boolean) {
    return {
      type: 'QUOTE_STALE' as const,
      workspace,
      tabId,
      quoteId,
      hasFormChanged,
    };
  },
};
